// Drives an event-ticket gate check-in after a scan resolves to an event
// ticket (SLICE-EVENT-TICKET-PASS). On mount it POSTs the scanned token to the
// staff check-in endpoint (active → used, single-use) and renders a read-only
// result panel — green "Checked in" or a red state for an already-used /
// cancelled / unpaid ticket. Fired exactly once per mount (StrictMode guard),
// mirroring MembershipScanController.

import { useEffect, useRef, useState } from 'react'

import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import { errorMessageFor } from '../../owner/errorMessages.js'
import {
  StampOverlayShell,
  StampOverlaySkeleton,
} from '../../shared/stamp/StampOverlayChrome.js'
import '../../shared/membership/membership.css'

interface TicketCheckinResponse {
  card_id: string
  event_name: string
  tier_name: string
  seat: string | null
  holder_name: string | null
  used_at: string
  wallet_sync_failed?: boolean
}

export function EventTicketScanController({
  cardId,
  rawToken,
  onDone,
}: {
  cardId: string
  rawToken: string
  onDone: () => void
}) {
  const [data, setData] = useState<TicketCheckinResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    ownerApi
      .post<TicketCheckinResponse>(
        `/staff/cards/${encodeURIComponent(cardId)}/ticket/check-in`,
        { stamp_token: rawToken },
      )
      .then((res) => setData(res))
      .catch((err) => {
        setError(
          err instanceof OwnerApiError
            ? errorMessageFor(err.code)
            : 'Could not check in this ticket. Try scanning again.',
        )
      })
      .finally(() => setLoading(false))
    // One-shot on mount; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <StampOverlayShell ariaLabelledBy="ticket-verify-name">
        <div id="ticket-verify-name" className="stamp-overlay-name">
          Not valid
        </div>
        <p className="stamp-overlay-hint" style={{ margin: '0 auto 18px' }}>
          {error}
        </p>
        <div className="stamp-overlay-actions">
          <button type="button" className="owner-btn" onClick={onDone}>
            Close
          </button>
        </div>
      </StampOverlayShell>
    )
  }

  if (loading || !data) {
    return (
      <StampOverlayShell ariaLabelledBy="ticket-verify-name">
        <StampOverlaySkeleton onCancel={onDone} />
      </StampOverlayShell>
    )
  }

  return (
    <StampOverlayShell ariaLabelledBy="ticket-verify-name">
      <div className="membership-verify-eyebrow">{data.event_name}</div>
      <div id="ticket-verify-name" className="stamp-overlay-name">
        ✓ Checked in
      </div>

      <div className="membership-verify-status">
        <span className="membership-pill membership-pill-active">
          <span className="membership-pill-dot" aria-hidden="true" />
          Admitted
        </span>
      </div>

      <dl className="membership-verify-fields">
        <div className="membership-verify-field">
          <dt>Tier</dt>
          <dd>{data.tier_name}</dd>
        </div>
        {data.seat && (
          <div className="membership-verify-field">
            <dt>Seat</dt>
            <dd>{data.seat}</dd>
          </div>
        )}
        {data.holder_name && (
          <div className="membership-verify-field">
            <dt>Ticket holder</dt>
            <dd>{data.holder_name}</dd>
          </div>
        )}
      </dl>

      <div className="stamp-overlay-actions">
        <button
          type="button"
          className="owner-btn owner-btn-primary owner-btn-block"
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </StampOverlayShell>
  )
}
